import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { permVisible } from "../lib/permissions";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Plus, Trash, PencilSimple, FileArrowUp, CheckCircle, Warning, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRIORITY_STYLES = {
  low: "bg-slate-600",
  medium: "bg-amber-600",
  high: "bg-red-600",
};

const URGENCY_LABEL = {
  none: "",
  soft: "Reminder",
  medium: "Coming up",
  high: "Due soon",
  critical: "DUE TODAY",
  overdue: "OVERDUE",
};

const URGENCY_STYLE = {
  soft: "bg-slate-500/20 border-slate-500 text-slate-200",
  medium: "bg-amber-500/20 border-amber-500 text-amber-200",
  high: "bg-orange-600/25 border-orange-500 text-orange-200",
  critical: "bg-red-600/30 border-red-500 text-red-100 animate-pulse",
  overdue: "bg-red-700/40 border-red-600 text-red-50 animate-pulse",
};

function next_sunday_iso() {
  const now = new Date();
  const dow = now.getDay();
  const daysToAdd = dow === 0 ? 7 : 7 - dow;
  const d = new Date(now);
  d.setDate(d.getDate() + daysToAdd);
  return d.toISOString().slice(0, 10);
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

const emptyForm = () => ({
  title: "",
  description: "",
  priority: "medium",
  assigned_to: "",
  due_date: next_sunday_iso(),
  repeat_weekly: false,
});

export default function Tasks() {
  const { user } = useAuth();
  // Anyone granted the Tasks module manages tasks; only admins get the
  // permission-filtered assignee list from /auth/users.
  const isAdmin = permVisible(user, "tasks");
  const isRealAdmin = user?.role === "admin";
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [filterUser, setFilterUser] = useState("all");
  const [showImport, setShowImport] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tasks");
      setTasks(data || []);
      if (isRealAdmin) {
        const u = await api.get("/auth/users");
        // Only accounts that actually have access to Tasks (admin, or `tasks`
        // in their granular permissions) are eligible as assignees.
        const eligible = (u.data || []).filter(
          (x) => x.role === "admin" || (Array.isArray(x.permissions) && x.permissions.includes("tasks")),
        );
        setUsers(eligible);
      } else {
        // Non-admins with the Tasks allowance can assign too — /team is the
        // name-only roster (no emails or permissions).
        const t = await api.get("/team?perm=tasks");
        setUsers(t.data || []);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      title: t.title || "",
      description: t.description || "",
      priority: t.priority || "medium",
      assigned_to: t.assigned_to || "",
      due_date: t.due_date || next_sunday_iso(),
      repeat_weekly: !!t.repeat_weekly,
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.assigned_to) { toast.error("Choose an assignee"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/tasks/${editing.id}`, form);
        toast.success("Task updated");
      } else {
        await api.post("/tasks", form);
        toast.success("Task created");
      }
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleComplete = async (t) => {
    try {
      await api.post(`/tasks/${t.id}/complete`, { completed: !t.completed });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"?`)) return;
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.success("Task deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = useMemo(() => {
    let list = tasks;
    if (isAdmin && filterUser !== "all") {
      list = list.filter((t) => t.assigned_to === filterUser);
    }
    return list;
  }, [tasks, filterUser, isAdmin]);

  const openTasks = filtered.filter((t) => !t.completed);
  const doneTasks = filtered.filter((t) => t.completed);
  const topUrgency = openTasks.reduce((acc, t) => {
    const order = ["none", "soft", "medium", "high", "critical", "overdue"];
    return order.indexOf(t.urgency) > order.indexOf(acc) ? t.urgency : acc;
  }, "none");

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Reminder banner */}
      {openTasks.length > 0 && topUrgency !== "none" && (
        <div
          className={`border rounded-md px-4 py-3 flex items-center gap-3 ${URGENCY_STYLE[topUrgency] || ""}`}
          data-testid="tasks-reminder-banner"
        >
          <Warning size={22} weight="fill" />
          <div className="flex-1">
            <div className="font-semibold text-sm uppercase tracking-wide">{URGENCY_LABEL[topUrgency]}</div>
            <div className="text-sm mt-0.5">
              {topUrgency === "overdue" && "You have overdue tasks. Please complete or reassign them."}
              {topUrgency === "critical" && "Tasks due today. Sunday is the deadline."}
              {topUrgency === "high" && "Sunday is close — start wrapping these up."}
              {topUrgency === "medium" && "Halfway to Sunday. Time to make progress."}
              {topUrgency === "soft" && "New week — here's what's on your plate."}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl">Tasks</h2>
          <div className="label-mono">{isAdmin ? "Assign and track weekly tasks" : "Your assigned tasks — due every Sunday"}</div>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowImport(true)} data-testid="tasks-import-btn">
              <FileArrowUp size={16} className="mr-2" /> Import from Google Doc
            </Button>
            <Button onClick={openCreate} data-testid="tasks-new-btn">
              <Plus size={16} className="mr-2" /> New task
            </Button>
          </div>
        )}
      </div>

      {/* Admin filter */}
      {isAdmin && (
        <div className="flex items-center gap-3">
          <Label className="label-mono">Filter by person</Label>
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger className="w-64" data-testid="tasks-filter-user">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All people</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {loading ? (
        <div className="label-mono">Loading…</div>
      ) : (
        <>
          <TaskList
            title={`Open (${openTasks.length})`}
            items={openTasks}
            isAdmin={isAdmin}
            onToggle={toggleComplete}
            onEdit={openEdit}
            onDelete={remove}
            emptyMsg={isAdmin ? "No open tasks. Create one to get started." : "No open tasks — great work."}
          />
          <TaskList
            title={`Completed (${doneTasks.length})`}
            items={doneTasks}
            isAdmin={isAdmin}
            onToggle={toggleComplete}
            onEdit={openEdit}
            onDelete={remove}
            emptyMsg="No completed tasks yet."
            muted
          />
        </>
      )}

      {/* Create / edit dialog (admin) */}
      {isAdmin && (
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-lg" data-testid="task-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit task" : "New task"}</DialogTitle>
              <DialogDescription>Due date defaults to the upcoming Sunday.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="label-mono">Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" />
              </div>
              <div>
                <Label className="label-mono">Description</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-mono">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-mono">Due date (Sunday recommended)</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due" />
                </div>
              </div>
              <div>
                <Label className="label-mono">Assign to</Label>
                <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                  <SelectTrigger data-testid="task-assignee"><SelectValue placeholder="Choose person…" /></SelectTrigger>
                  <SelectContent>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name || u.email} — {u.role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between border-t pt-3">
                <div>
                  <div className="text-sm font-medium">Repeat weekly</div>
                  <div className="text-xs text-muted-foreground">Auto-recreate every Sunday for the next cycle.</div>
                </div>
                <Switch checked={form.repeat_weekly} onCheckedChange={(v) => setForm({ ...form, repeat_weekly: v })} data-testid="task-repeat" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="task-save">{saving ? "Saving…" : (editing ? "Update" : "Create")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Import dialog */}
      {isAdmin && (
        <ImportDocDialog
          open={showImport}
          onClose={() => setShowImport(false)}
          users={users}
          onImported={() => { setShowImport(false); load(); }}
        />
      )}
    </div>
  );
}

function TaskList({ title, items, isAdmin, onToggle, onEdit, onDelete, emptyMsg, muted }) {
  return (
    <div className="space-y-2">
      <div className="label-mono">{title}</div>
      {items.length === 0 ? (
        <div className={`text-sm ${muted ? "text-muted-foreground" : "text-muted-foreground"}`}>{emptyMsg}</div>
      ) : (
        <div className="space-y-2">
          {items.map((t) => (
            <TaskRow key={t.id} t={t} isAdmin={isAdmin} onToggle={onToggle} onEdit={onEdit} onDelete={onDelete} muted={muted} />
          ))}
        </div>
      )}
    </div>
  );
}

function TaskRow({ t, isAdmin, onToggle, onEdit, onDelete, muted }) {
  const urgencyBar = t.urgency && t.urgency !== "none" ? URGENCY_STYLE[t.urgency] : "border-border";
  return (
    <div
      className={`border rounded-md p-3 flex items-start gap-3 ${urgencyBar} ${muted ? "opacity-70" : ""}`}
      data-testid={`task-row-${t.id}`}
    >
      <Checkbox
        checked={!!t.completed}
        onCheckedChange={() => onToggle(t)}
        className="mt-1"
        data-testid={`task-check-${t.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`font-medium text-sm ${t.completed ? "line-through text-muted-foreground" : ""}`} data-testid={`task-title-${t.id}`}>{t.title}</span>
          <Badge className={`${PRIORITY_STYLES[t.priority] || "bg-slate-600"} text-white text-[10px] uppercase`}>{t.priority || "medium"}</Badge>
          {t.repeat_weekly && <Badge variant="outline" className="text-[10px]">Weekly</Badge>}
          {!t.completed && t.urgency !== "none" && (
            <Badge className={`text-[10px] uppercase ${t.urgency === "overdue" || t.urgency === "critical" ? "bg-red-600" : t.urgency === "high" ? "bg-orange-500" : t.urgency === "medium" ? "bg-amber-500" : "bg-slate-500"}`}>
              {URGENCY_LABEL[t.urgency]}
            </Badge>
          )}
        </div>
        {t.description && <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{t.description}</div>}
        <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          <span className="flex items-center gap-1"><Clock size={12} /> Due {fmtDate(t.due_date)}</span>
          <span>Assigned to <span className="text-foreground">{t.assigned_to_name || "—"}</span></span>
          {t.completed && t.completed_at && (
            <span className="flex items-center gap-1 text-emerald-500"><CheckCircle size={12} /> Completed {new Date(t.completed_at).toLocaleDateString()}</span>
          )}
        </div>
      </div>
      {isAdmin && (
        <div className="flex flex-col gap-1 shrink-0">
          <Button size="sm" variant="ghost" onClick={() => onEdit(t)} data-testid={`task-edit-${t.id}`}>
            <PencilSimple size={14} />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(t)} data-testid={`task-delete-${t.id}`}>
            <Trash size={14} className="text-red-500" />
          </Button>
        </div>
      )}
    </div>
  );
}

function ImportDocDialog({ open, onClose, users, onImported }) {
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [proposed, setProposed] = useState(null);
  const [saving, setSaving] = useState(false);

  const parse = async () => {
    if (!text.trim()) { toast.error("Paste the doc text first"); return; }
    setParsing(true);
    try {
      const { data } = await api.post("/tasks/import-doc", { text });
      setProposed(data.tasks || []);
      if ((data.tasks || []).length === 0) toast.info("No tasks extracted from that text.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setParsing(false); }
  };

  const changeAssignee = (i, uid) => {
    setProposed((prev) => {
      const p = [...prev];
      const u = users.find((x) => x.id === uid);
      p[i] = { ...p[i], assigned_to: uid, assigned_to_name: u ? (u.name || u.email) : "" };
      return p;
    });
  };

  const changeField = (i, field, value) => {
    setProposed((prev) => {
      const p = [...prev];
      p[i] = { ...p[i], [field]: value };
      return p;
    });
  };

  const confirm = async () => {
    const ready = (proposed || []).filter((t) => t.title && t.assigned_to);
    if (ready.length === 0) { toast.error("Assign at least one task before creating"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/tasks/import-doc/confirm", { tasks: ready });
      toast.success(`Created ${data.created} task(s)`);
      setText(""); setProposed(null);
      onImported();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const reset = () => { setText(""); setProposed(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-3xl" data-testid="tasks-import-dialog">
        <DialogHeader>
          <DialogTitle>Import tasks from a Google Doc</DialogTitle>
          <DialogDescription>
            Open the Google Doc, select all (Ctrl/Cmd+A), copy, then paste here. Claude will parse
            names → tasks and match each to the right person. Review before creating.
          </DialogDescription>
        </DialogHeader>

        {!proposed ? (
          <>
            <Textarea
              rows={12}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste the doc text here…"
              data-testid="tasks-import-textarea"
            />
            <DialogFooter>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={parse} disabled={parsing} data-testid="tasks-import-parse">
                {parsing ? "Parsing with AI…" : "Parse tasks"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="text-sm text-muted-foreground">
              Review the {proposed.length} extracted task(s). Confirm assignees and edit as needed.
              Any task without an assignee will be skipped.
            </div>
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {proposed.map((t, i) => (
                <div key={i} className="border rounded-md p-3 space-y-2" data-testid={`tasks-import-row-${i}`}>
                  <Input value={t.title} onChange={(e) => changeField(i, "title", e.target.value)} className="font-medium" />
                  <Textarea rows={2} value={t.description || ""} onChange={(e) => changeField(i, "description", e.target.value)} placeholder="Description" />
                  <div className="grid grid-cols-3 gap-2 items-center">
                    <div>
                      <Label className="label-mono">Assignee {t.assignee_hint && <span className="text-muted-foreground">(hint: {t.assignee_hint})</span>}</Label>
                      <Select value={t.assigned_to || ""} onValueChange={(v) => changeAssignee(i, v)}>
                        <SelectTrigger data-testid={`tasks-import-assignee-${i}`}><SelectValue placeholder="Choose…" /></SelectTrigger>
                        <SelectContent>
                          {users.map((u) => (
                            <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="label-mono">Priority</Label>
                      <Select value={t.priority} onValueChange={(v) => changeField(i, "priority", v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="label-mono">Due date</Label>
                      <Input type="date" value={t.due_date} onChange={(e) => changeField(i, "due_date", e.target.value)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={reset}>Start over</Button>
              <Button onClick={confirm} disabled={saving} data-testid="tasks-import-confirm">
                {saving ? "Creating…" : `Create ${proposed.filter((t) => t.assigned_to).length} task(s)`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
